/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 *
 * Segmented "Cars | Models" switch for the listing pages: show every individual
 * car, or one card per unique make+model. The choice persists in localStorage
 * and is shared between the marketplace and the tenant storefront.
 */

import { useState } from 'react';
import { Car as CarIcon, LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type ViewMode = 'cars' | 'models';

const STORAGE_KEY = 'mezw_view_mode';

/** View-mode state persisted in localStorage; `def` applies on first visit. */
export function useViewMode(def: ViewMode): [ViewMode, (m: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'cars' || saved === 'models' ? saved : def;
  });
  const set = (m: ViewMode) => {
    setMode(m);
    localStorage.setItem(STORAGE_KEY, m);
  };
  return [mode, set];
}

export default function ViewModeToggle({
  mode,
  onChange,
}: {
  mode: ViewMode;
  onChange: (m: ViewMode) => void;
}) {
  const { t } = useTranslation();
  const btn = (m: ViewMode, label: string, icon: JSX.Element) => (
    <button
      type="button"
      onClick={() => onChange(m)}
      aria-pressed={mode === m}
      className={`flex items-center gap-1.5 px-4 py-1.5 rounded-full text-sm font-semibold transition-colors ${
        mode === m ? 'bg-yellow-500 text-black' : 'text-gray-600 hover:text-gray-900'
      }`}
    >
      {icon} {label}
    </button>
  );

  return (
    <div className="inline-flex items-center bg-gray-100 rounded-full p-1">
      {btn('cars', t('view.cars'), <CarIcon className="h-4 w-4" />)}
      {btn('models', t('view.models'), <LayoutGrid className="h-4 w-4" />)}
    </div>
  );
}
